// src/app/services/terms.service.ts
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, combineLatest, map } from 'rxjs';

import { User } from '../../models/user.model';

import { ApiService } from '../core/api.service';
import { NetworkService } from '../core/network.service';
import { AuthService } from '../auth/auth.service';


export interface Terms {
  id: number;
  publish_at: string;
  text: string;
  markdown: string;
}

@Injectable({ providedIn: 'root' })
export class TermsService {
  private _blockingRequired$ = new BehaviorSubject<boolean>(false);
  readonly blockingRequired$;

  private _latestTerms$ = new BehaviorSubject<Terms | null>(null);
  latestTerms$ = this._latestTerms$.asObservable();

  // track current network state
  private isOnline = true;

  constructor(
    
    private api: ApiService,
    private network: NetworkService,
    private auth: AuthService,
  ) {
    this.blockingRequired$ = combineLatest([
      this._blockingRequired$, this.auth.impersonation$, this.auth.user$,
    ]).pipe(map(([required, impersonation, user]) => required && impersonation === null && user !== null));
    // Clear the previous account's gate before impersonation is cleared on logout.
    this.auth.user$.subscribe(user => {
      if (!user) {
        this._blockingRequired$.next(false);
        this._latestTerms$.next(null);
      }
    });
    // keep a live snapshot of connectivity
    this.network.online$.subscribe((online) => {
      this.isOnline = online;
    });
  }

  /** Call this whenever you have a fresh User from /profile/me */
  async updateFromUser(user: User): Promise<void> {
    await this.auth.ready;
    if (this.auth.isImpersonating || !this.auth.user) {
      this._blockingRequired$.next(false);
      this._latestTerms$.next(null);
      return;
    }
    // 👇 only run this logic if we are online
    if (!this.isOnline) {
      // optionally: you could log or noop here
      return;
    }
    const needsBlock =
      user.current_t_and_c_id != null &&
      user.t_and_c_id !== user.current_t_and_c_id;

    this._blockingRequired$.next(needsBlock);

    if (needsBlock && user.current_t_and_c_id) {
      await this.loadTermsById(user.current_t_and_c_id);
    }
  }

  private async loadTermsById(id: number): Promise<void> {
    // adjust endpoint string to match your backend
    const terms = await this.api.get<Terms>(`terms/${id}`);
    this._latestTerms$.next(terms);
  }

  /** Accept latest terms (backend should set t_and_c_id = current_t_and_c_id) */
  async acceptLatest(): Promise<void> {
    // adjust endpoint string to match your backend
    await this.api.post<unknown>('terms/accept', {});
    this._blockingRequired$.next(false);


  }
}
